"use client";

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

// Tab variant types
type TabVariant = "border" | "boxed" | "lift";

type TabsContextType = {
  activeTab: string | number;
  setActiveTab: (id: string | number) => void;
  registerTabPanel: (id: string | number) => void;
  variant: TabVariant;
};

const TabsContext = createContext<TabsContextType | undefined>(undefined);

export function useTabs() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error("useTabs must be used within a TabsProvider");
  }
  return context;
}

export function TabsProvider({
  children,
  defaultValue,
  onTabChange,
  variant = "border", // Default to border variant
  controlledValue,
}: {
  children: React.ReactNode;
  defaultValue?: string | number;
  onTabChange?: (id: string | number) => void;
  variant?: TabVariant;
  controlledValue?: string | number;
}) {
  const [activeTab, setActiveTabState] = useState<string | number>(
    defaultValue || "0"
  );

  // Use a ref for tab panels to avoid state updates that cause re-renders
  const tabPanelsRef = useRef<Set<string | number>>(new Set());

  // Use controlled value if provided, otherwise use internal state
  const currentActiveTab =
    controlledValue !== undefined ? controlledValue : activeTab;

  const onTabChangeRef = useRef(onTabChange);
  onTabChangeRef.current = onTabChange;

  const setActiveTab = useCallback((id: string | number) => {
    // Only update internal state if not in controlled mode
    if (controlledValue === undefined) {
      setActiveTabState(id);
    }
    onTabChangeRef.current?.(id);
  }, [controlledValue]);

  const registerTabPanel = useCallback((id: string | number) => {
    tabPanelsRef.current.add(id);
  }, []);

  const contextValue = useMemo(
    () => ({
      activeTab: currentActiveTab,
      setActiveTab,
      registerTabPanel,
      variant,
    }),
    [currentActiveTab, setActiveTab, registerTabPanel, variant]
  );

  return (
    <TabsContext.Provider value={contextValue}>
      {children}
    </TabsContext.Provider>
  );
}
